import React, { ChangeEvent, useEffect, useState, useMemo } from 'react';
import {
  Stack,
  Button,
  Typography,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Checkbox, SelectChangeEvent,
} from '@mui/material';
import { Field, Formik, Form, useFormikContext } from 'formik';
import { useNavigate } from 'react-router-dom';
import employeeReviewModel, { employeeReviewType } from '../forms/models/employeeReviewModel';
import {
  EmployeeReviewData,
  usePostAddEmployeeReviewMutation,
  useGetEmployeeReviewsByUserByUserIdQuery,
} from '../../redux/vmsApi';
import employeeReviewSchema from '../forms/schemas/employeeReviewSchema';
import PageWithHeader from "../common/PageWithHeader";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {getAvailableMonthsForSubmission} from "../../utils/periodUtil";
import {useRouter} from "../../hooks/useRouter";
import {useAppSelector} from "../../redux/hooks";

type MyFormValues = employeeReviewType;

const employeeReviewModelInitial = employeeReviewModel.initialValues;

type Props = {
  onSuccess?: (invite: EmployeeReviewData) => void;
};

const {
  formField: {
    reviewPeriod,
    quarterlyGoals,
    quarterlyObjective,
    areaToImprove,
    examplesOfExcellence,
    mainGoalToAchieve,
    turnUpOnTime,
    dailyCheckIn,
    reportsOnTime,
    accomplishmentTitle,
    accomplishmentRating,
    quarterlyTitle,
    quarterlyRating,
    monthlyTitle,
    monthlyIncentiveGoal,
  },
} = employeeReviewModel;

// Check if a monthly period (e.g., "March 2026") is March 2026 or later
// Monthly Incentive Goals feature started in March 2026
const isMonthMarch2026OrLater = (monthPeriod: string): boolean => {
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                       'July', 'August', 'September', 'October', 'November', 'December'];
  const match = monthPeriod.match(/^(\w+)\s+(\d{4})$/);
  if (!match) return false;
  const [, monthName, yearStr] = match;
  const year = parseInt(yearStr);
  const monthIndex = monthNames.indexOf(monthName); // 0-11
  // March 2026 = year 2026, monthIndex 2
  // Returns true if (year > 2026) OR (year === 2026 AND monthIndex >= 2)
  return (year > 2026) || (year === 2026 && monthIndex >= 2);
};

const ratingOptions = [
  { value: undefined, label: 'None', color: 'Transparent' },
  { value: 'A+', label: 'A+', color: 'Platinum' },
  { value: 'A', label: 'A', color: 'Diamond' },
  { value: 'A-', label: 'A-', color: 'Gold' },
  { value: 'B+', label: 'B+', color: 'Silver' },
  { value: 'B', label: 'B', color: 'Emerald' },
  { value: 'C', label: 'C', color: 'Jade' },
  { value: 'C-', label: 'C-', color: 'Copper' },
  { value: 'D', label: 'D', color: 'Bronze' },
  { value: 'E', label: 'E', color: 'Brass' },
  { value: 'F', label: 'F', color: 'Wood' },
];

const SPEARFields: React.FC<{ monthOptions: string[] }> = ({ monthOptions }) => {
  const { errors, touched, values, setFieldValue } = useFormikContext<any>();

  const textFieldVariant = 'outlined';

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setFieldValue(name, checked);
  };

  const [quarterNumber, setQuarterNumber] = useState('1');
  const [quarterYear, setQuarterYear] = useState(new Date().getFullYear().toString());

  const getQuarterAndYearFromDate = (reviewPeriodValue: string): { quarter: string; year: string } => {
    const parts = reviewPeriodValue.match(/(\w+)\s(\d{4})/);
    if (!parts) return { quarter: '1', year: new Date().getFullYear().toString() }; // Default if parsing fails

    const [, monthName, year] = parts;
    const month = new Date(`${monthName} 1, ${year}`).getMonth();
    const quarter = Math.ceil((month + 1) / 3).toString();
    return { quarter, year };
  };


  useEffect(() => {
    const { quarter, year } = getQuarterAndYearFromDate(values[reviewPeriod.name]);
    setQuarterNumber(quarter);
    setQuarterYear(year);
  }, [values]);

  return (
    <Stack width="100%" spacing={4}>
      <Field
        {...reviewPeriod}
        as={FormControl}
        fullWidth
        error={touched[reviewPeriod.name] && Boolean(errors[reviewPeriod.name])}
      >
        <InputLabel>Review Period</InputLabel>
        <Select
          {...reviewPeriod}
          value={values[reviewPeriod.name]}
          displayEmpty
          inputProps={{ 'aria-label': 'Review Period' }}
          autoComplete="off"
          onChange={(event: SelectChangeEvent) => {
            setFieldValue(reviewPeriod.name, event.target.value);
          }}
        >
          {monthOptions.map((quarter, index) => (
            <MenuItem key={index} value={quarter}>{quarter}</MenuItem>
          ))}
        </Select>
        {touched[reviewPeriod.name] && errors[reviewPeriod.name] && (
          <FormHelperText>{String(errors[reviewPeriod.name])}</FormHelperText>
        )}
      </Field>
      <Typography>CEO TOP 5 accomplishments duties & responsibilities</Typography>
      <Grid container spacing={2} justifyContent="center" alignContent="center">
        {[...Array(5)].map((_, index) => (
          <Grid item xs={12} container key={index} spacing={2}>
            <Grid item xs={1} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="h6" component="div">
                {index + 1}
              </Typography>
            </Grid>
            <Grid item xs={7}>
              <TextField
                variant={textFieldVariant}
                fullWidth
                type="textarea"
                multiline
                autoComplete="off"
                {...accomplishmentTitle}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  let updatedAccomplishments = values.accomplishments ? values.accomplishments.slice() : [];
                  if (!updatedAccomplishments[index]) {
                    updatedAccomplishments[index] = { title: '', rating: '' };
                  }
                  updatedAccomplishments[index].title = e.target.value;
                  setFieldValue('accomplishments', updatedAccomplishments);
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel id={`rating-label-${index}`}>{accomplishmentRating.label}</InputLabel>
                <Field
                  {...accomplishmentRating}
                  as={Select}
                  {...accomplishmentRating}
                  name={`${accomplishmentRating.name}${index}`}
                  value={values.accomplishments[index]?.rating || ''}
                  onChange={(event: SelectChangeEvent) => {
                    const updatedAccomplishments = values.accomplishments.slice();
                    if (!updatedAccomplishments[index]) {
                      updatedAccomplishments[index] = { title: '', rating: '' };
                    }
                    updatedAccomplishments[index].rating = event.target.value;
                    setFieldValue('accomplishments', updatedAccomplishments);
                  }}
                >
                  {ratingOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Field>
              </FormControl>
            </Grid>
          </Grid>
        ))}
      </Grid>
      <Typography>OKR's for Q{quarterNumber} {quarterYear}</Typography>
      <Grid item xs={7}>
        <Field
          as={TextField}
          variant={textFieldVariant}
          fullWidth
          {...quarterlyObjective}
          type="text"
          multiline
          maxRows={4}
          autoComplete="off"
          error={touched[quarterlyObjective.name] && Boolean(errors[quarterlyObjective.name])}
          helperText={(touched[quarterlyObjective.name] && errors[quarterlyObjective.name]) ? errors[quarterlyObjective.name] : ''}
        />
      </Grid>
      <Grid container spacing={2} justifyContent="center" alignContent="center">
        {[...Array(4)].map((_, index) => (
          <Grid item xs={12} container key={index} spacing={2}>
            <Grid item xs={1} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="h6" component="div">
                KR{index + 1}
              </Typography>
            </Grid>
            <Grid item xs={7}>
              <Field
                as={TextField}
                variant={textFieldVariant}
                fullWidth
                {...quarterlyTitle}
                type="textarea"
                multiline
                autoComplete="off"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  let updatedQuarterlyGoals = values.quarterlyGoals.slice();
                  if (!updatedQuarterlyGoals[index]) {
                    updatedQuarterlyGoals[index] = { title: '', rating: '' };
                  }
                  updatedQuarterlyGoals[index].title = e.target.value;
                  setFieldValue('quarterlyGoals', updatedQuarterlyGoals);
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel id={`rating-label-${index}`}>{quarterlyRating.label}</InputLabel>
                <Field
                  {...quarterlyRating}
                  as={Select}
                  name={`${quarterlyGoals.name}${index}`}
                  value={values.quarterlyGoals[index]?.rating || ''}
                  onChange={(event: SelectChangeEvent) => {
                    const updatedQuarterlyGoals = values.quarterlyGoals.slice();
                    if (!updatedQuarterlyGoals[index]) {
                      updatedQuarterlyGoals[index] = { title: '', rating: '' };
                    }
                    updatedQuarterlyGoals[index].rating = event.target.value;
                    setFieldValue('quarterlyGoals', updatedQuarterlyGoals);
                  }}
                >
                  {ratingOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Field>
              </FormControl>
            </Grid>
          </Grid>
        ))}
      </Grid>
      <Typography>Next Month's Goals</Typography>
      <Grid container spacing={2} justifyContent="center" alignContent="center">
        {[...Array(4)].map((_, index) => (
          <Grid item xs={12} container key={index} spacing={2}>
            <Grid item xs={1} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="h6" component="div">
                {index + 1}
              </Typography>
            </Grid>
            <Grid item xs={11}>
              <Field
                as={TextField}
                variant={textFieldVariant}
                fullWidth
                {...monthlyTitle}
                type="textarea"
                multiline
                autoComplete="off"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  let updatedMonthlyGoals = values.monthlyGoals.slice();
                  if (!updatedMonthlyGoals[index]) {
                    updatedMonthlyGoals[index] = { title: '' };
                  }
                  updatedMonthlyGoals[index].title = e.target.value;
                  setFieldValue('monthlyGoals', updatedMonthlyGoals);
                }}
              />
            </Grid>
          </Grid>
        ))}
      </Grid>
      
      {/* Monthly Incentive Goal Section - Only show for March 2026 onwards */}
      {isMonthMarch2026OrLater(values[reviewPeriod.name]) && (
      <Grid container spacing={2} sx={{ 
        mt: 2, 
        p: 3, 
        bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
        borderRadius: 2,
        border: '2px solid #4caf50',
      }}>
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ color: '#2e7d32', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
            🎯 Monthly Incentive Goal (50% Salary Bonus)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Set your key goal for this month. Achieving this goal makes you eligible for a 50% salary bonus incentive.
            Your CEO/Manager will evaluate and record your achievement percentage.
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <TextField
            variant={textFieldVariant}
            fullWidth
            label="My Monthly Incentive Goal"
            placeholder="Describe the specific goal you commit to achieving this month..."
            multiline
            rows={2}
            value={values.monthlyIncentiveGoal?.description || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setFieldValue('monthlyIncentiveGoal', {
                ...values.monthlyIncentiveGoal,
                description: e.target.value,
              });
            }}
          />
        </Grid>
        <Grid item xs={12} sm={8}>
          <TextField
            variant={textFieldVariant}
            fullWidth
            label="How Will Success Be Measured?"
            placeholder="Describe how this goal's achievement will be measured (e.g., revenue target, number of sales, project delivery)..."
            multiline
            rows={2}
            value={values.monthlyIncentiveGoal?.measure || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setFieldValue('monthlyIncentiveGoal', {
                ...values.monthlyIncentiveGoal,
                measure: e.target.value,
              });
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            variant={textFieldVariant}
            fullWidth
            type="number"
            label="Target (%)"
            placeholder="100"
            value={values.monthlyIncentiveGoal?.targetPercentage ?? 100}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setFieldValue('monthlyIncentiveGoal', {
                ...values.monthlyIncentiveGoal,
                targetPercentage: parseInt(e.target.value) || 100,
              });
            }}
            InputProps={{
              inputProps: { min: 0, max: 200 }
            }}
            helperText="Target percentage to achieve (default 100%)"
          />
        </Grid>
      </Grid>
      )}

      <Field
        as={TextField}
        variant={textFieldVariant}
        fullWidth
        {...areaToImprove}
        type="text"
        multiline
        maxRows={4}
        autoComplete="off"
        error={touched[areaToImprove.name] && Boolean(errors[areaToImprove.name])}
        helperText={(touched[areaToImprove.name] && errors[areaToImprove.name]) ? errors[areaToImprove.name] : ''}
      />
      <Field
        as={TextField}
        variant={textFieldVariant}
        fullWidth
        {...examplesOfExcellence}
        type="textarea"
        multiline
        maxRows={4}
        autoComplete="off"
        error={touched[examplesOfExcellence.name] && Boolean(errors[examplesOfExcellence.name])}
        helperText={(touched[examplesOfExcellence.name] && errors[examplesOfExcellence.name]) ? errors[examplesOfExcellence.name] : ''}
      />
      <Field
        as={TextField}
        variant={textFieldVariant}
        fullWidth
        {...mainGoalToAchieve}
        type="text"
        multiline
        maxRows={4}
        autoComplete="off"
        error={touched[mainGoalToAchieve.name] && Boolean(errors[mainGoalToAchieve.name])}
        helperText={(touched[mainGoalToAchieve.name] && errors[mainGoalToAchieve.name]) ? String(errors[mainGoalToAchieve.name]) : ''}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={values[turnUpOnTime.name]}
            onChange={handleCheckboxChange}
          />
        }
        {...turnUpOnTime}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={values[dailyCheckIn.name]}
            onChange={handleCheckboxChange}
          />
        }
        {...dailyCheckIn}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={values[reportsOnTime.name]}
            onChange={handleCheckboxChange}
          />
        }
        {...reportsOnTime}
      />
    </Stack>
  );
};

const SPEAR: React.FC<Props> = () => {
  const [createEmployeeReview] = usePostAddEmployeeReviewMutation();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const currentUser = useAppSelector(state => state.auth.user);

  const router = useRouter();

  // Fetch existing SPEAR submissions to filter available months
  const { data: mySubmissions } = useGetEmployeeReviewsByUserByUserIdQuery(
    { userId: currentUser?.id ?? '' },
    { skip: !currentUser?.id }
  );

  // Compute available months (filter out submitted ones)
  const monthOptions = useMemo(() => {
    return getAvailableMonthsForSubmission(mySubmissions);
  }, [mySubmissions]);

  const initialValues: MyFormValues = {
    ...employeeReviewModelInitial,
  };

  const handleSubmit = async (values: MyFormValues, { resetForm }: { resetForm: () => void }) => {
    try {
      await createEmployeeReview({ employeeReviewData: values }).unwrap();
      setIsSubmitted(true);
      resetForm();
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };


  return (
    <PageWithHeader>
      {!isSubmitted ? (
        <Formik enableReinitialize initialValues={initialValues} onSubmit={handleSubmit} validationSchema={employeeReviewSchema}>
          <Form>
            <Stack spacing={4} width="100%" p={10} pb={5}>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Button color="primary" onClick={() => router.push('/reviews')}>
                    <ArrowBackIcon/>
                    <Typography color="primary" align="left">Dashboard</Typography>
                  </Button>
                </Grid>
                <Grid item xs={6} container justifyContent="flex-end">
                  <Button color="primary" onClick={() => router.push('/reviews/spear/historic')}>
                    <Typography color="primary" align="right">View Historic Submissions</Typography>
                  </Button>
                </Grid>
              </Grid>
              <Typography variant='h1' gutterBottom style={{fontWeight: 'bold', color: '#123456'}}>
                Self Performance Evaluation and Review (SPEAR)
              </Typography>
              <Typography variant="body1" gutterBottom>
                The Self Performance Evaluation and Review is designed to help you reflect on your accomplishments,
                areas of excellence, and goals for the upcoming quarter. Please fill in the review period,
                evaluate your top five accomplishments based on your job description, set quarterly goals and plan next month's goals,
                and identify areas for improvement. Your performance will be assessed using a rating system, and you'll
                also be asked to set a main goal to achieve. Consistent attendance, daily check-ins, and timely report
                submissions are also part of this evaluation.
              </Typography>
              <SPEARFields monthOptions={monthOptions} />
              <Button variant="contained" type="submit">
                Submit
              </Button>
            </Stack>
          </Form>
        </Formik>
      ) : (
        <Stack spacing={4} width="100%" p={10} pb={5}>
          <Typography variant='h5' gutterBottom style={{color: '#123456'}}>
            Submission Successful!
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={true}
                disabled
              />
            }
            label="Thank you for your submission."
          />
          <Button
            variant="contained"
            color="primary"
            onClick={() => router.push('/reviews')}
          >
            Back to Dashboard
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setIsSubmitted(false)}
          >
            Submit Another
          </Button>
        </Stack>
      )}
    </PageWithHeader>
  );
};

export default SPEAR;
